import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const INDIA_TIME_ZONE = 'Asia/Kolkata';

const normalizeBoolean = (value) => {
  if (value === true || value === 1 || String(value).toLowerCase() === 'true' || String(value) === '1') {
    return true;
  }
  if (value === false || value === 0 || String(value).toLowerCase() === 'false' || String(value) === '0') {
    return false;
  }
  return null;
};

const isIndiaPaymentWindowDate = () => {
  const day = Number(new Intl.DateTimeFormat('en-US', {
    timeZone: INDIA_TIME_ZONE,
    day: 'numeric'
  }).format(new Date()));

  return day >= 10 && day <= 15;
};

const SubscriptionGuard = ({ children }) => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');
  const [showActivationScreen, setShowActivationScreen] = useState(false);
  const subscriptionStatusRequestInFlight = useRef(false);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    console.log('SubscriptionGuard useEffect triggered, user:', user);
    if (user) {
      checkSubscriptionStatus();
    } else {
      console.log('No user found, setting loading to false');
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;

    const refreshSubscriptionStatus = () => {
      if (document.visibilityState === 'visible') {
        checkSubscriptionStatus();
      }
    };

    const refreshInterval = window.setInterval(refreshSubscriptionStatus, 2 * 60 * 1000);
    window.addEventListener('focus', refreshSubscriptionStatus);
    document.addEventListener('visibilitychange', refreshSubscriptionStatus);

    return () => {
      window.clearInterval(refreshInterval);
      window.removeEventListener('focus', refreshSubscriptionStatus);
      document.removeEventListener('visibilitychange', refreshSubscriptionStatus);
    };
  }, [user]);

  const checkSubscriptionStatus = async () => {
    if (subscriptionStatusRequestInFlight.current) return;
    subscriptionStatusRequestInFlight.current = true;

    console.log('Checking subscription status for user:', user?.email);
    console.log('Making API call to:', '/api/payment/subscription-status');
    
    try {
      const response = await axios.get('/api/payment/subscription-status');
      console.log('Subscription status API response:', response.data);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Subscription status API call failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
      
      setError('Failed to check subscription status');
      
      // For coaches/editors, assume subscription is required but not active if API fails
      if (user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR')) {
        console.log('User is SYSTEM_EDITOR, setting subscription required but not active');
        setSubscriptionStatus({
          requiresSubscription: true,
          subscriptionActive: false
        });
      } else {
        console.log('User is not SYSTEM_EDITOR, allowing access');
        setSubscriptionStatus({
          requiresSubscription: false,
          subscriptionActive: true
        });
      }
    } finally {
      subscriptionStatusRequestInFlight.current = false;
      setLoading(false);
    }
  };

  const initiatePayment = async () => {
    setPaymentLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/payment/initiate-subscription');
      const { orderId, amount, currency, keyId } = response.data;
      
      console.log('Payment initiation successful:', response.data);
      
      // Configure Razorpay options
      const options = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: "CalorieNSweat",
        description: "Platform Subscription",
        order_id: orderId,
        handler: function (response) {
          handlePaymentSuccess(response);
        },
        prefill: {
          name: user.firstName + ' ' + (user.lastName || ''),
          email: user.email,
          contact: user.phone || ''
        },
        theme: {
          color: "#4A90E2"
        },
        modal: {
          ondismiss: function() {
            setPaymentLoading(false);
            setError('Payment was cancelled. Please complete the payment to continue using the platform.');
          }
        },
        // RESTRICT PAYMENT METHODS - Only allow card and UPI
        config: {
          display: {
            blocks: {
              methods: {
                name: "Payment Methods",
                instruments: [
                  {
                    method: "card"
                  },
                  {
                    method: "upi"
                  },
                  {
                    method: "netbanking"
                  }
                ]
              }
            },
            hide: [
              {
                method: "wallet"
              },
              {
                method: "emi"
              },
              {
                method: "paylater"
              }
            ]
          }
        }
      };

      // Open Razorpay checkout
      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Failed to initiate payment:', error);
      setError('Failed to initiate payment. Please try again.');
      setPaymentLoading(false);
    }
  };

  const handlePaymentSuccess = async (response) => {
    console.log('Payment successful:', response);
    
    try {
      // Verify payment on backend
      const verifyResponse = await axios.post('/api/payment/verify-payment', null, {
        params: {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature
        }
      });
      
      if (verifyResponse.data.success) {
        console.log('Payment verification successful');
        // Refresh subscription status and reload page
        await checkSubscriptionStatus();
        setShowActivationScreen(false);
        window.location.reload();
      } else {
        setError('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      setError('Payment verification failed. Please contact support if payment was deducted.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--gray-light)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <div style={{ fontSize: '18px', color: 'var(--dark-blue)' }}>
            Checking subscription status...
          </div>
        </div>
      </div>
    );
  }

  console.log('=== SUBSCRIPTION GUARD RENDER ===');
  console.log('User:', user?.email);
  console.log('User System Roles:', user?.systemRoles?.map(r => r.code));
  console.log('User Member Role:', user?.memberRole?.code);
  console.log('Subscription Status:', subscriptionStatus);
  console.log('Loading:', loading);
  console.log('Error:', error);
  console.log('Has SYSTEM_EDITOR role:', user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR'));
  console.log('Has SYSTEM_CUSTOMER role:', user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER'));

  const renderPaymentPage = ({ advancePayment = false } = {}) => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: 'var(--gray-light)',
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--white)',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>💳</div>
        
        <h1 style={{
          color: 'var(--dark-blue)',
          marginBottom: '20px',
          fontSize: '28px'
        }}>
          {advancePayment ? 'Payment Window Open' : 'Subscription Required'}
        </h1>

        <div style={{
          background: 'var(--pale-blue)',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '30px',
          border: '2px solid var(--primary-blue)'
        }}>
          <p style={{
            color: 'var(--dark-blue)',
            fontSize: '16px',
            marginBottom: '15px',
            fontWeight: '500'
          }}>
            {advancePayment
              ? 'The monthly payment window is open. You can renew now for the next billing cycle.'
              : 'Your subscription is expired/inactive. Please make payment to continue.'}
          </p>
          
          <div style={{
            background: 'var(--white)',
            padding: '15px',
            borderRadius: '6px',
            marginBottom: '15px'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--primary-blue)' }}>
              ₹1,180 per month
            </div>
            <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
              Platform Fee: ₹1,000 + GST: ₹180 (9% CGST + 9% SGST)
            </div>
          </div>

          <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
            <strong>Subscription includes:</strong>
            <ul style={{ textAlign: 'left', marginTop: '10px', marginLeft: '20px' }}>
              <li>Full access to CalorieNSweat platform</li>
              <li>Customer management tools</li>
              <li>Invoice generation</li>
              <li>Health tracking features</li>
              <li>WhatsApp notifications</li>
            </ul>
          </div>
        </div>

        {error && (
          <div style={{
            background: '#FED7D7',
            color: '#C53030',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #FEB2B2'
          }}>
            {error}
          </div>
        )}

        <button
          onClick={initiatePayment}
          disabled={paymentLoading}
          style={{
            background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--dark-blue) 100%)',
            color: 'white',
            border: 'none',
            padding: '15px 30px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: paymentLoading ? 'not-allowed' : 'pointer',
            width: '100%',
            marginBottom: '20px',
            opacity: paymentLoading ? 0.7 : 1
          }}
        >
          {paymentLoading ? 'Processing...' : 'Pay Now - ₹1,180'}
        </button>

        {advancePayment && (
          <button
            type="button"
            onClick={() => {
              setShowActivationScreen(false);
              setError('');
            }}
            disabled={paymentLoading}
            style={{
              background: 'transparent',
              color: 'var(--gray-dark)',
              border: '1px solid var(--gray-medium)',
              padding: '12px 20px',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: paymentLoading ? 'not-allowed' : 'pointer',
              width: '100%',
              marginBottom: '20px'
            }}
          >
            Back to Dashboard
          </button>
        )}

        <div style={{
          fontSize: '12px',
          color: 'var(--gray-dark)',
          lineHeight: '1.5'
        }}>
          <p>🔒 Secure payment powered by Razorpay</p>
          <p>You will be redirected to Razorpay to complete the payment</p>
          <p>After successful payment, your subscription will be activated immediately</p>
          <p>💡 Billing cycle: Payment due on 15th of every month</p>
        </div>
        
      </div>
    </div>
  );

  const renderChildrenWithPaymentBanner = () => {
    const paymentWindowOpen = normalizeBoolean(subscriptionStatus?.paymentWindowOpen);
    const currentMonthPaymentMade = normalizeBoolean(subscriptionStatus?.currentMonthPaymentMade);
    const showPaymentWindowBanner = paymentWindowOpen === true
      && currentMonthPaymentMade === false
      && isIndiaPaymentWindowDate()
      && subscriptionStatus?.requiresSubscription === true
      && user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR');

    if (!showPaymentWindowBanner) {
      return children;
    }

    return (
      <div>
        <div
          style={{
            width: '100%',
            marginTop: '52px',
            marginBottom: '20px',
            padding: '0 20px'
          }}
        >
          <button
            type="button"
            onClick={() => {
              setShowActivationScreen(true);
              setError('');
            }}
            style={{
              width: '100%',
              minHeight: '64px',
              border: '1px solid rgba(255, 255, 255, 0.32)',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #1D4ED8 0%, #0F766E 100%)',
              color: 'white',
              padding: '14px 22px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 12px 28px rgba(29, 78, 216, 0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              textAlign: 'left',
              lineHeight: '1.35'
            }}
          >
            <span>
              <span style={{ display: 'block', fontSize: '18px' }}>Payment Window Open</span>
              <span style={{ display: 'block', fontSize: '13px', fontWeight: '500', opacity: 0.92 }}>
                Renew your platform subscription for the next billing cycle.
              </span>
            </span>
            <span style={{
              background: 'rgba(255, 255, 255, 0.18)',
              border: '1px solid rgba(255, 255, 255, 0.28)',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}>
              Pay Now
            </span>
          </button>
        </div>
        {children}
      </div>
    );
  };

  // If user doesn't require subscription (customers don't need subscription)
  if (user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER')) {
    console.log('SYSTEM_CUSTOMER detected - allowing access without subscription check');
    return children;
  }

  if (showActivationScreen) {
    return renderPaymentPage({ advancePayment: true });
  }

  // Temporary emergency access rule: use only the subscription active flag.
  // Normalize the value because some API/database paths return "true" or 1.
  const subscriptionActive = normalizeBoolean(subscriptionStatus?.subscriptionActive);
  if (subscriptionActive === true) {
    console.log('ALLOWING ACCESS - Subscription is active');
    return renderChildrenWithPaymentBanner();
  }

  console.log('BLOCKING ACCESS - Subscription active flag is not true:', subscriptionStatus?.subscriptionActive);
  return renderPaymentPage();
};

export default SubscriptionGuard;
